import { useUserInfo } from "@/api/user";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useMemo } from "react";

export const useHasPermission = (...requiredPermissions: string[]) => {
  const { data: userInfo } = useUserInfo();

  const hasAccess = useMemo(() => {
    return requiredPermissions.every((permission) =>
      userInfo?.permissions.includes(permission),
    );
  }, [userInfo?.permissions]);

  return hasAccess;
};

export const useHasPlan = (...requiredPlans: string[]) => {
  const { data: userInfo } = useUserInfo();

  if (requiredPlans.length === 0) {
    return true;
  }

  return requiredPlans.some(
    (plan) => userInfo?.subscription?.plan_name === plan,
  );
};

export const useHasFlags = (...requiredFlags: string[]) => {
  const flags = useFlags<Flags>();

  return requiredFlags.every((flag) => flags[flag as keyof Flags]);
};

export const usePermissions = () => {
  const { data: userInfo } = useUserInfo();

  return userInfo?.permissions || [];
};
interface PermissionsProps {
  requiredPermissions?: string[];
  requiredPlans?: string[];
  requiredFlags?: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const Permissions: React.FC<PermissionsProps> = ({
  requiredPermissions = [],
  requiredPlans = [],
  requiredFlags = [],
  children,
  fallback = null,
}) => {
  const hasPermission = useHasPermission(...requiredPermissions);
  const hasPlan = useHasPlan(...requiredPlans);
  const hasFlags = useHasFlags(...requiredFlags);

  if (hasPermission && hasPlan && hasFlags) {
    return children;
  }

  return fallback;
};
